// Bereich „Aufgaben": frontend/src/pages/Tasks.tsx, pages/Kanban.tsx,
// pages/Gantt.tsx, components/TaskDrawer.tsx, components/TaskWizard.tsx.
// Diese Datei und die Spiegel-Datei in ../en gehören dem Aufgaben-Worker.
//
// `offeneAufgaben` ist das Muster für Plural + Interpolation: eine FUNKTION,
// keine Platzhalter-Syntax. Der Compiler prüft damit Aufrufstelle und
// Übersetzung mit; eine ICU-Zeichenkette wäre für ihn nur ein String.
//
// Einige Strings (Speichern, Abbrechen, Löschen …) kommen auch in anderen
// Bereichen vor. Sie stehen bewusst HIER und nicht in `common`: `common` ist
// während der parallelen Wellen eingefroren, die Konsolidierung macht der
// Projektleiter danach (docs/i18n.md §4.1).
export const tasks = {
  seitenTitel: "Aufgaben",
  offeneAufgaben: (n: number) => (n === 1 ? "1 offene Aufgabe" : `${n} offene Aufgaben`),
  keineAufgaben: "Keine Aufgaben",

  /** Unterstrich-Reiter der Aufgaben-Seite. */
  reiter: {
    kanban: "Kanban",
    kalender: "Kalender",
    zeitachse: "Zeitachse",
  },

  kanban: {
    titel: "Kanban",
    spalteIngest: "Ingest",
    spalteOffen: "Offen",
    spalteInArbeit: "In Arbeit",
    spalteReview: "Review",
    spalteErledigt: "Erledigt",
    spalteProjekt: "Projekt",

    ladenFehlgeschlagen: "Laden fehlgeschlagen",
    sfNichtVerschiebbar:
      "Salesforce-Aufgaben können nicht verschoben werden — in Salesforce bearbeiten.",
    workstreamReihenfolgeFehler: (grund: string) =>
      `Workstream-Reihenfolge speichern fehlgeschlagen: ${grund} — lade neu`,
    reihenfolgeFehler: (grund: string) =>
      `Reihenfolge speichern fehlgeschlagen: ${grund} — lade neu`,
    speichernFehler: (grund: string) => `Speichern fehlgeschlagen: ${grund} — lade neu`,

    aufgabeZuweisen: "Aufgabe zuweisen",
    filter: "Filter",
    filterUmschalten: "Filter umschalten",
    neuLaden: "reload",
    projektFiltern: "Projekt filtern …",
    projektFilter: "Projekt-Filter",
    pilleMeineTasks: "meine Tasks",
    pilleVonMirZugewiesen: "Von mir zugewiesen",
    pilleDone: "Done",
    pilleSalesforce: "Salesforce",
    zuweisen: "Zuweisen",
    nurMeine: "Nur meine",
    vonMirZugewiesenTitel: "Aufgaben, die ich anderen zugewiesen habe",
    vonMirZugewiesen: "Von mir zugewiesen",
    erledigte: "Erledigte",
    salesforce: "Salesforce",

    leerEigeneTitel: "Keine eigenen Aufgaben",
    leerEigeneHinweis:
      "Dir sind aktuell keine Aufgaben zugewiesen — es gibt aber welche von anderen.",
    alleAufgabenZeigen: "Alle Aufgaben zeigen",
    leerGefiltertTitel: (filter: string) => `Keine Aufgaben für „${filter}"`,
    leerHinweis: "Passe die Filter oben an oder lege eine neue Aufgabe an.",
    filterZuruecksetzen: "Filter zurücksetzen",

    projektUmsortieren: "Projekt umsortieren (ziehen)",
    projektUmsortierenAria: (projekt: string) => `Projekt ${projekt} umsortieren`,
    zieleEinAusklappen: "Projekt-Ziele ein-/ausklappen",
    ziele: "Ziele",
    statusReiterAria: (projekt: string) => `${projekt} status tabs`,
    aufgabeHinzufuegen: "Aufgabe hinzufügen",

    ideeEinwerfen: "Idee einwerfen",
    intakeLeer: "(leer)",
    intakeFragen: (n: number) => (n === 1 ? `${n} Frage` : `${n} Fragen`),
    laedt: "lädt…",

    workstreamUmsortieren: "Workstream umsortieren (ziehen)",
    workstreamUmsortierenAria: (name: string) => `Workstream ${name} umsortieren`,
    workstreamEinklappen: "Workstream einklappen",
    workstreamAusklappen: "Workstream ausklappen",
    workstreamKontextOeffnen: "Workstream-Kontext oeffnen",
    ohneWorkstream: "ohne Workstream",
    workstreamFallback: "Workstream",
    prioritaetTitel: (p: number) => `Priority ${p}`,

    ueberfaellig: "überfällig",
    faelligAm: "Fällig am",
    erinnerungAm: (datum: string) => `Erinnerung: ${datum}`,
    inSalesforceOeffnen: "In Salesforce öffnen",
    zugewiesenVon: (wer: string) => `von ${wer}`,
    zugewiesenAn: (wer: string) => `→ ${wer}`,
    wartetAufAntwort: "Wartet auf Antwort",
    brauchtEntscheidung: "braucht Entscheidung",
    rework: (runde: number) => `Rework #${runde}`,
    wartetAufTasks: (n: number) => (n === 1 ? `wartet auf ${n} Task` : `wartet auf ${n} Tasks`),
  },

  /** Ziele-Panel unter der Projekt-Lane. */
  ziele: {
    laden: "Ziele laden…",
    projektZiel: "Projekt-Ziel",
    bearbeiten: "bearbeiten",
    platzhalter: "Overarching Goal des Projekts (1-3 Sätze)…",
    keinZiel: "noch kein Projekt-Ziel gesetzt",
    architektur: "Architektur",
    workstreams: "Workstreams",
    workstreamOhneZiel: " — kein Ziel (via 📓 Workstream-Doku setzen)",
  },

  /** Workstream-Doku (phases.context_md) im zentrierten Editor-Modal.
   *  Die VORLAGE selbst bleibt bewusst deutsch — sie ist Inhalt einer Datei,
   *  die die Loop-Sessions lesen, keine Beschriftung der Oberfläche. */
  workstreamDoku: {
    verwerfenFrage: "Ungespeicherte Aenderungen verwerfen?",
    verwerfenText: "Der Editor-Inhalt geht verloren.",
    ueberschreibenFrage: "Aktueller Inhalt wird ueberschrieben — fortfahren?",
    ueberschreiben: "Ueberschreiben",
    kopf: (name: string) => `Workstream-Context: ${name}`,
    kopfUnterzeile:
      "Lebendige Workstream-Doku — Loop-Sessions lesen in PERCEIVE, aktualisieren in WRAP-UP.",
    prio: "Prio",
    prioAria: "Workstream-Priority (1 hoechste, 100 niedrigste)",
    prioTitel:
      "Workstream-Priority 1..100 (1=hoechste). Wirkt auf next-claimable + Sort im Kanban.",
    zeilen: (ist: number, budget: number) => `${ist}/${budget} Zeilen`,
    budgetUeberschritten:
      "Soft-Budget ueberschritten — beim naechsten Wrap-Up kondensieren",
    budget: "Soft-Budget 80 Zeilen",
    schliessenAria: "Schliessen",
    vorschau: "Vorschau",
    markdown: "Markdown",
    vorlageEinfuegen: "Vorlage einfügen",
    leer: "(leer)",
    editorPlatzhalter:
      "# Workstream-CLAUDE.md leer — druecke oben rechts 'Vorlage einfügen' um zu starten.",
    editorAria: "Workstream-Markdown-Editor",
    handoffs: (n: number) => `Handoffs (${n})`,
    ungespeichert: "ungespeichert",
    zuletztGeaendert: (wann: string) => `zuletzt geaendert ${wann}`,
    schliessen: "Schliessen",
    workstreamNamePlatzhalter: "<Workstream-Name>",
  },

  /** Laufende Agent-Sessions in der Projekt-Lane. */
  session: {
    stale: "stale",
    staleTitel: (min: number) => `stale — seit ${min} min kein Lebenszeichen`,
    ok: "ok",
    wrapSoon: "wrap soon",
    wrapNow: "wrap NOW",
    erledigt: (n: number) => `${n} done`,
    letztesUpdate: "Minuten seit letztem Board-Update",
    live: "live",
  },

  zeitachse: {
    titel: "Zeitachse",
    mobilTitel: "Zeitachse auf Desktop empfohlen",
    mobilText:
      "Die Gantt-Ansicht (Projekt-Sidebar + Task-Tree + Zeit-Achse) braucht mehr Platz als ein Phone-Display bietet. Oeffne diese Seite auf einem Desktop oder Tablet im Landscape-Modus.",
    zuKanban: "Zu Kanban",
    zuKalender: "Zu Kalender",
    mobilFuss: "Beide Views unterstuetzen mobile Bedienung.",
    kopf: (n: number, von: string, bis: string) =>
      `${n} Aufgaben · ${von} bis ${bis} · gruppiert nach Projekt`,
    projektFilter: "Projekt-Filter",
    alleProjekte: "Alle Projekte",
    erledigteAusblenden: "Erledigte ausblenden",
    spalteProjektAufgabe: "Projekt / Aufgabe",
    heute: "Heute",
    leer: "Keine Aufgaben im sichtbaren Zeitraum.",
    ohneProjekt: "ohne Projekt",
    offen: "offen",
    balkenOffen: (titel: string, start: string) =>
      `${titel} · ${start} → offen (kein Enddatum)`,
    balken: (titel: string, start: string, ende: string) =>
      `${titel} · ${start} → ${ende}`,
    status: {
      new: "Neu",
      open: "Offen",
      ongoing: "Laufend",
      in_progress: "In Arbeit",
      done: "Erledigt",
      closed: "Geschlossen",
      lost: "Verloren",
      "on hold": "Pausiert",
      feedback: "Feedback",
    },
  },

  /** Anlegen/Bearbeiten/Zuweisen einer Aufgabe (TaskWizard). */
  wizard: {
    titelBearbeiten: "Aufgabe bearbeiten",
    titelZuweisen: "Aufgabe zuweisen",
    titelNeu: "Neue Aufgabe",
    aktionSpeichern: "Speichern",
    aktionZuweisen: "Zuweisen",
    aktionAnlegen: "Aufgabe anlegen",
    loeschenFrage: (titel: string) => `Aufgabe „${titel}" löschen?`,

    dateiTypNichtUnterstuetzt: "Dateityp nicht unterstützt",
    dateiTypErlaubt: "Erlaubt: PDF, Markdown, TXT",
    dateiAblegen: "Datei hier ablegen (PDF, Markdown, TXT)",
    zugewiesenToast: (n: number) =>
      n === 1 ? `${n} Mitarbeiter zugewiesen` : `${n} Mitarbeitern zugewiesen`,
    speichernFehlgeschlagen: "Aufgabe speichern fehlgeschlagen",
    zuweisenFehlgeschlagen: "Zuweisen fehlgeschlagen",
    anlegenFehlgeschlagen: "Aufgabe anlegen fehlgeschlagen",
    loeschenFehlgeschlagen: "Aufgabe löschen fehlgeschlagen",

    feldTitel: "Titel",
    titelPlatzhalter: "Was soll erledigt werden?",
    feldBeschreibung: "Beschreibung",
    optional: "(optional)",
    beschreibungPlatzhalter: "Details, Akzeptanzkriterien, Notizen …",

    zuweisenAn: "Zuweisen an",
    zuweisenAnHinweis: "(optional — leer = für mich)",
    ganzeAbteilung: "Ganze Abteilung zuweisen",
    alleAbteilungen: "Alle Abteilungen",
    alleAbteilungenEntfernen: "Alle Abteilungen entfernen",
    abteilungEntfernen: (abteilung: string) => `${abteilung} entfernen`,
    persoenlich: "Persönlich",
    persoenlichVorNicht: " — Empfänger darf die Aufgabe ",
    persoenlichNicht: "nicht",
    persoenlichNachNicht: " weiterreichen",
    persoenlichStandard: " (Standard: weiterreichbar)",

    start: "Start",
    startAria: "Startdatum (TT.MM.JJJJ)",
    faellig: "Fällig",
    faelligAria: "Faelligkeitsdatum (TT.MM.JJJJ)",

    erinnerung: "Erinnerung",
    erinnerungTitel: "Erste/einzige Erinnerung (TT.MM.JJJJ)",
    erinnerungAria: "Erinnerungsdatum (TT.MM.JJJJ)",
    intervallKeine: "Keine Erinnerung",
    intervallEinmalig: "Einmalig (an einem Tag)",
    intervallTaeglich: "Täglich",
    intervallWoechentlich: "Wöchentlich",
    intervallZweiwoechentlich: "Alle 2 Wochen",
    intervallMonatlich: "Monatlich",
    intervallCustom: "Benutzerdefiniert (alle N Tage)",
    alle: "Alle",
    tageErinnern: "Tage erinnern",

    workstream: "Workstream",
    keinWorkstream: "— Kein Workstream —",

    anhaenge: "Anhänge",
    linkPlatzhalter: "Link einfügen (https://…)",
    link: "Link",
    linkEntfernen: "Link entfernen",
    laedt: "lädt…",
    datei: "Datei",
  },

  /** Aufgaben-Detail (TaskDrawer). */
  drawer: {
    modalTitel: "Aufgabe",
    laden: "loading…",

    sfSchreibgeschuetzt: "Salesforce-Aufgabe — schreibgeschützt",
    sfHinweis: "Diese Aufgabe in Salesforce bearbeiten. Atlas spiegelt sie nur.",
    sfOeffnen: "In Salesforce öffnen",

    ansichtUmschalten: "Ansicht umschalten",
    ansichtKompakt: "Kompakt",
    ansichtDetail: "Detail",
    ansichtKompaktTitel: "Kompakte Ansicht",
    ansichtDetailTitel: "Detaillierte Ansicht",

    entscheidungZuKurz: "Bitte gib deine Entscheidung als kurzen Text ein (min. 3 Zeichen).",
    erinnerungVerschoben: "Erinnerung verschoben",
    weitergereichtAn: "Weitergereicht an",
    linkAnhaengenFehler: "Link anhängen fehlgeschlagen",
    dokumentAnhaengenFehler: "Dokument anhängen fehlgeschlagen",
    anhangEntfernenFehler: "Anhang entfernen fehlgeschlagen",

    // Bestand: dieser Text steht seit jeher englisch da (auch in der deutschen
    // Oberfläche) — er wird beim Umbau NICHT umformuliert (docs/i18n.md §4.3).
    loeschenFrage: (titel: string) => `Delete task "${titel}"?`,
    loeschenAktion: "Delete task",

    brauchtEntscheidungFett: "Braucht menschliche Entscheidung.",
    brauchtEntscheidungText:
      "Der Decider hat eskaliert (irreversibel/extern oder Rework-Cap erschöpft) — setze den Status oben (Offen = zur Umsetzung freigeben, Erledigt = schließen).",

    feldTitel: "Titel",
    titelAria: "Aufgaben-Titel",
    status: "Status",
    statusOffen: "Offen",
    statusInArbeit: "In Arbeit",
    statusReview: "Review (Prüfung)",
    statusFeedback: "Feedback (wartet auf mich)",
    statusErledigt: "Erledigt",
    prioritaet: "Priorität",
    prioritaetTitel: "1 = höchste, 100 = niedrigste; 50 = default",
    prioritaetAria: "Priority (1 hoechste, 100 niedrigste)",
    faelligAm: "Fällig am",
    faelligAria: "Faelligkeitsdatum (TT.MM.JJJJ)",
    faelligTitel: "Fällig am (TT.MM.JJJJ)",
    faelligGesperrt:
      "Nur der Ersteller kann die Fälligkeit ändern — du kannst die Erinnerung verschieben.",

    ziel: "Ziel (ein Satz, klare Zielaussage)",
    zielAria: "Goal (ein Satz, klare Zielaussage)",
    zielPlatzhalter:
      "z.B. 'Director-Layer iter2: alle LIFESTYLE-Slides nutzen bullets_with_image mit demographic-pool-Rotation'",
    acceptance: "Acceptance-Kriterien (eines pro Zeile, messbar)",
    acceptancePlatzhalter:
      "- bullets_with_image bei pos 6+7 gerendert\n- pool[0] und pool[1] sichtbar verschiedene Personen\n- Vision-Audit-Score >= 6.0",
    beschreibung: "Beschreibung / Notizen",
    beschreibungAria: "Description / Notes",
    beschreibungPlatzhalter: "—",

    unteraufgaben: "Unteraufgaben",
    unteraufgabenStand: (fertig: number, gesamt: number) => `(${fertig}/${gesamt})`,
    unteraufgabe: "Unteraufgabe",
    unteraufgabePflicht: "Sub-Task braucht Titel, Goal und mind. 1 Acceptance-Kriterium.",
    unteraufgabeFehler: "Sub-Task-Anlage fehlgeschlagen",
    unteraufgabeStatusFehler: "Sub-Status-Change fehlgeschlagen",
    unteraufgabeWiederOeffnen: "Klick: wieder öffnen",
    unteraufgabeErledigt: "Klick: erledigt",
    unteraufgabeTitelPlatzhalter: "Titel der Unteraufgabe (knapp, imperativ)",
    unteraufgabeTitelAria: "Sub-Task Titel",
    unteraufgabeZielPlatzhalter: "Ziel (1 Satz — messbar)",
    unteraufgabeZielAria: "Sub-Task Goal",
    unteraufgabeAcceptancePlatzhalter:
      "Acceptance-Kriterien (eines pro Zeile, BINAER checkbar)\nz.B. - Endpoint POST /xxx liefert 201\n- Test scripts/test_xxx.py gruen",
    unteraufgabeAcceptanceAria: "Sub-Task Acceptance-Kriterien",
    unteraufgabePrioAria: "Sub-Task Priority",
    unteraufgabeLeer:
      "keine Unteraufgaben. Mehrschrittige Ziele: erst Unteraufgaben anlegen, dann arbeiten.",

    workstream: "Workstream",
    workstreamAria: "Workstream-Zuordnung",
    keinWorkstream: "— Kein Workstream —",
    workstreamNeuTitel: "Neuen Workstream in diesem Projekt anlegen",
    workstreamNeuAria: "Neuen Workstream anlegen",
    workstreamNamePlatzhalter: "z.B. 'Director-Iteration', 'Cost-Tracking', 'Frontend-Polish'",
    workstreamNameAria: "Neuer Workstream-Name",
    workstreamFehler: "Workstream-Anlage fehlgeschlagen",

    fragt: (wer: string) => `${wer} fragt`,
    freitextPlatzhalter: "optional: weitere Anmerkung (wird mit der Wahl gespeichert)…",
    freitextAria: "Freitext-Antwort (optional)",

    eskalationKopf: (wer: string) => `Braucht deine Entscheidung — ${wer} hat eskaliert`,
    eskalationEmpfehlung: "Empfehlung des Deciders:",
    entscheidungPflicht: "Deine Entscheidung (Pflicht)",
    entscheidungAria: "Deine Entscheidung",
    entscheidungPlatzhalter:
      "Anweisung/Begründung — wird als bindende Entscheidung gespeichert; die nächste Session befolgt sie…",
    entscheidungSenden: "Entscheidung senden & freigeben",
    entscheidungSchliessen: "Mit Entscheidung schließen",
    entscheidungFuss:
      "„Freigeben\" → Status offen + höchste Priorität, der Loop zieht sie als nächstes. „Schließen\" → erledigt, ohne weitere Umsetzung.",
    mastermindKopf: "Braucht deine Entscheidung — der Mastermind hat an dich eskaliert",
    mastermindPlatzhalter:
      "Was soll passieren? — wird als bindende Entscheidung gespeichert; die nächste Session befolgt sie…",
    mastermindFuss:
      "„Freigeben\" → Status offen + höchste Priorität, der Loop zieht sie als nächstes und liest deine Entscheidung. „Schließen\" → erledigt.",
    mastermind: "Mastermind",
    mastermindRunde: (n: number) => `Runde ${n}`,
    mastermindEskaliert: "eskaliert",
    mastermindAufgeloest: "aufgelöst",
    fuerDenMenschen: "Für den Menschen",

    spec: "Spec",
    haengtAbVon: (n: number) => (n === 1 ? `Hängt ab von ${n} Task` : `Hängt ab von ${n} Tasks`),
    nonGoals: "Non-Goals",
    verifikation: "Verifikation",
    visualQuality: "Visual-Quality-Kriterien",
    scope: "Scope",

    review: (verdikt: string) => `Review: ${verdikt}`,
    reviewRunde: (runde: number, pruefer: string) => `Runde ${runde} · ${pruefer}`,
    reviewsGesamt: (n: number) => ` · ${n} Reviews gesamt`,
    reviewFix: (fix: string) => ` → Fix: ${fix}`,
    reviewZurueck: "Zurück in „Offen\" zur Nacharbeit — der Loop claimt sie erneut.",

    evidence: (n: number) => `Evidence (${n})`,
    evidenceVergroessern: "Vergrößern",

    activity: "Activity",
    dateiAnhaengen: "Datei anhängen",
    laedt: "lädt…",
    activityLeer:
      "Noch keine Activity. Sobald eine Session arbeitet, erscheinen Comments, Ergebnisse und Rueckfragen hier.",
    optionen: "Optionen: ",
    oeffnen: "open",

    zugewiesenVonAn: "Zugewiesen von",
    zugewiesenAn: "an",
    weiterreichbar: "weiterreichbar",
    persoenlich: "persönlich",
    erinnerung: "Erinnerung",
    heuteFaellig: "🔔 heute fällig",
    erinnerungKeine: "Keine Erinnerung",
    erinnerungEinmalig: "einmalig",
    erinnerungAlleTage: (n: number) => `alle ${n} Tage`,
    erinnerungNaechste: (intervall: string, datum: string) =>
      `${intervall}, nächste am ${datum}`,
    intervallAria: "Reminder-Intervall",
    intervallKeine: "Keine",
    intervallEinmalig: "Einmalig",
    intervallTaeglich: "Täglich",
    intervallWoechentlich: "Wöchentlich",
    intervallZweiwoechig: "Alle 2 Wochen",
    intervallMonatlich: "Monatlich",
    intervallCustom: "Benutzerdef.",
    naechsteErinnerungAria: "Naechste Erinnerung (TT.MM.JJJJ)",
    naechsteErinnerungTitel: "Nächste/erste Erinnerung (TT.MM.JJJJ)",
    alle: "Alle",
    tage: "Tage",
    verschieben: "Verschieben",
    dreiTage: "+3 Tage",
    eineWoche: "+1 Woche",
    verschiebenAria: "Verschieben auf (TT.MM.JJJJ)",
    verschiebenTitel: "Erinnerung verschieben auf (TT.MM.JJJJ)",
    uebernehmen: "Übernehmen",
    weiterreichen: "Weiterreichen",
    weiterreichenTitel: "An eine andere Person weiterreichen",
    weiterreichenGesperrt: "Persönlich — nicht weiterreichbar",
    uebergebenAn: (wer: string) => `An ${wer} übergeben`,

    anhaenge: "Anhänge (Docs & Links)",
    dokument: "Dokument",
    anhangEntfernen: "Anhang entfernen",
    linkPlatzhalter: "Link anhängen (https://…)",
    link: "Link",
    dateiAnhaengenKnopf: (datei: string) => `„${datei}" anhängen`,
    datei: "Datei",
  },
};

export type TasksKatalog = typeof tasks;
